import { useEffect } from 'react';
import { getArchetypeDefinition } from '../archetypes';
import { TuvSudLogo } from '../components/ui/TuvSudLogo';
import { getAllMarketConfigs } from '../landingpages/marketConfig';
import { staticPages } from '../landingpages/registry';
import { applyPageMeta } from '../lib/applyPageMeta';
import { getPublicLandingPagePath } from '../routing';

const overviewPages = getAllMarketConfigs().map((page) => {
  const staticPage = staticPages[page.slug];
  const archetype = staticPage?.archetype ? getArchetypeDefinition(staticPage.archetype) : null;
  const publicPath = getPublicLandingPagePath(page.slug);

  return {
    ...page,
    archetype,
    publicPath,
  };
});

type OverviewPageItem = (typeof overviewPages)[number];

const overviewLabels: Record<string, string> = {
  'aca-de-qm': 'QM Landing Page Germany',
  'aca-es-incompany': 'In Company Training Spain',
  'aca-in-accs': 'Advanced Certificate in Cyber Security India',
  'aca-in-icp': 'Industrial Cybersecurity Professional India',
  'aca-in-ciso': 'Chief Information Security Officer India',
  'aca-it-sustainability': 'Sustainability Masterclass Italy',
  'aca-us-fusa-machinery': 'FuSa Machinery USA',
};

const overviewDescriptions: Record<string, string> = {
  'aca-de-qm': 'German QM landing page for quality management and ISO 9001 training.',
  'aca-es-incompany': 'Spanish B2B landing page for In Company training programs.',
  'aca-in-accs':
    'Indian lead generation landing page for the Advanced Certificate in Cyber Security (ACCS) career path.',
  'aca-in-icp':
    'Indian lead generation landing page for the Industrial Cybersecurity Professional (ICP) career path.',
  'aca-in-ciso':
    'Indian lead generation landing page for the Chief Information Security Officer (CISO) career path.',
  'aca-it-sustainability':
    'Italian lead generation landing page for the Sustainability Managers and Practitioners Masterclass.',
  'aca-us-fusa-machinery':
    'US landing page for functional safety training for machinery and industrial systems.',
};

const cardTitle = (page: OverviewPageItem) => overviewLabels[page.slug] ?? page.label;

// Group landing pages by country, sorted alphabetically by country code (DE, ES, IN, IT, US),
// and within each country alphabetically by card title.
const countryGroups = [...new Set(overviewPages.map((page) => page.country))]
  .sort((a, b) => a.localeCompare(b))
  .map((code) => ({
    code,
    pages: overviewPages
      .filter((page) => page.country === code)
      .sort((a, b) => cardTitle(a).localeCompare(cardTitle(b))),
  }));

function CountrySection({ group }: { group: (typeof countryGroups)[number] }) {
  return (
    <section aria-label={`TÜV SÜD Academy ${group.code} landing pages`}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '12px',
          paddingBottom: '14px',
          marginBottom: '24px',
          borderBottom: '1px solid rgba(11, 37, 59, 0.12)',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 700 }}>TÜV SÜD Academy {group.code}</h2>
        <span style={{ fontSize: '13px', color: '#5c6f82' }}>
          {group.pages.length} {group.pages.length === 1 ? 'Landing Page' : 'Landing Pages'}
        </span>
      </div>
      <div className="overview-card-grid">
        {group.pages.map((page) => (
          <LandingPageCard key={page.slug} page={page} />
        ))}
      </div>
    </section>
  );
}

function LandingPageCard({ page }: { page: OverviewPageItem }) {
  return (
    <a
      href={`/${page.publicPath}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '28px',
        minHeight: '300px',
        borderRadius: '24px',
        textDecoration: 'none',
        color: 'inherit',
        background: '#ffffff',
        border: '1px solid rgba(11, 37, 59, 0.08)',
        boxShadow: '0 24px 60px rgba(11, 37, 59, 0.08)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: '12px',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: page.themeColor,
        }}
      >
        {page.country} / {page.language.toUpperCase()}
      </p>
      {page.archetype ? (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            width: 'fit-content',
            marginTop: '14px',
            padding: '6px 10px',
            borderRadius: '999px',
            background: 'rgba(0, 70, 173, 0.08)',
            color: '#0046ad',
            fontSize: '12px',
            fontWeight: 700,
          }}
        >
          {page.archetype.label}
        </div>
      ) : null}
      <h2
        style={{
          margin: '14px 0 10px',
          fontSize: '1.5rem',
          lineHeight: 1.2,
        }}
      >
        {overviewLabels[page.slug] ?? page.label}
      </h2>
      <p
        style={{
          margin: 0,
          lineHeight: 1.6,
          color: '#4e6478',
        }}
      >
        {overviewDescriptions[page.slug] ?? page.description}
      </p>
      <div
        style={{
          display: 'grid',
          gap: '8px',
          marginTop: '24px',
          padding: '14px',
          borderRadius: '14px',
          background: '#f3f7fb',
          color: '#345066',
          fontSize: '13px',
          lineHeight: 1.4,
        }}
      >
        <span>
          <strong>Alias:</strong> /{page.publicPath}
        </span>
        <span>
          <strong>Path:</strong> {page.slug}
        </span>
      </div>
      <div
        style={{
          marginTop: 'auto',
          paddingTop: '20px',
          fontWeight: 600,
          color: '#0046ad',
          marginBottom: 0,
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        Open landing page
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ flexShrink: 0 }}
        >
          <line x1="7" y1="17" x2="17" y2="7" />
          <polyline points="7 7 17 7 17 17" />
        </svg>
      </div>
    </a>
  );
}

export function OverviewPage() {
  useEffect(() => {
    applyPageMeta({
      title: 'TÜV SÜD Academy',
      description: 'TÜV SÜD Academy — professional training and certification programs.',
      themeColor: '#001a3a',
      ogImage: 'https://mango-grass-0099f4103.6.azurestaticapps.net/favicon.png',
      ogImageAlt: 'TÜV SÜD Academy',
      twitterCard: 'summary_large_image',
      robots: 'noindex,nofollow',
    });
  }, []);

  return (
    <main
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top left, rgba(0, 70, 173, 0.14), transparent 32%), linear-gradient(180deg, #f5f8fc 0%, #ffffff 55%)',
        color: '#0b253b',
      }}
    >
      <div
        style={{
          maxWidth: '1120px',
          margin: '0 auto',
          padding: '48px 24px 72px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '32px',
          }}
        >
          <TuvSudLogo width={72} />
          <div>
            <p
              style={{
                margin: 0,
                fontSize: '12px',
                textTransform: 'uppercase',
                color: '#5c6f82',
              }}
            >
              TÜV SÜD Academy
            </p>
            <h1
              style={{
                margin: '6px 0 0',
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                lineHeight: 1.05,
              }}
            >
              Landing Pages
            </h1>
          </div>
        </div>

        <p
          style={{
            maxWidth: '760px',
            margin: '0 0 40px',
            fontSize: '1.125rem',
            lineHeight: 1.6,
            color: '#345066',
          }}
        >
          Internal entry point for all currently available Academy landing pages. Each card shows the public alias and
          the internal storage path.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '56px' }}>
          {countryGroups.map((group) => (
            <CountrySection key={group.code} group={group} />
          ))}
        </div>
      </div>
    </main>
  );
}
